import { useState } from 'react'
import { useAppSelector, useAppDispatch } from '@/store/store-hooks'
import { selectResearchContactPromptCompleted } from '@/chat-common/store/chat-ux.selectors'
import { chatUxSlice } from '@/chat-common/store/chat-ux.slice'
import ResearchContactDialog from './ResearchContactDialog'
import { postResearchContact } from '@/agent-v2/feedback/fetch/post-research-contact'
import { useStore } from 'react-redux'
import clsx from 'clsx'
import { Copied, Copy, FileDownload, ThumbsDown, ThumbsUp } from '@/assets/icons'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MessageCopyStatus } from '@/agent-v2/components/MessageCopyStatus'
import { putAgentV2TurnFeedback } from '@/agent-v2/feedback/fetch/put-agent-v2-turn-feedback'
import type { AgentV2TurnFeedback } from '@/agent-v2/feedback/schemas'
import { useMessageCopy } from '@/agent-v2/hooks/useMessageCopy'
import MessageFeedbackDialog from '@/chat-common/components/dialogs/MessageFeedbackDialog'
import { FeedbackThumb } from '@/chat-common/schemas/feedback-schema'
import { IconButton } from '@/components/icon-button/IconButton'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import type { RootState } from '@/store/store'

type AgentV2ResponseActionsProps = {
  conversationId: string
  feedback: AgentV2TurnFeedback | null
  feedbackDisabledReason: string | null
  isFeedbackEligible: boolean
  onCopy: () => Promise<void>
  onFeedbackChange: (feedback: AgentV2TurnFeedback) => void
  turnId: string
  onDownload?: () => Promise<void>
}

export default function AgentV2ResponseActions({
  conversationId,
  feedback,
  feedbackDisabledReason,
  isFeedbackEligible,
  onCopy,
  onFeedbackChange,
  turnId,
  onDownload,
}: AgentV2ResponseActionsProps) {
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()
  const researchContactPromptCompleted = useAppSelector(selectResearchContactPromptCompleted)
  const store = useStore<RootState>()
  const [researchContactOpen, setResearchContactOpen] = useState(false)
  const [dialogPositive, setDialogPositive] = useState<boolean | null>(null)
  const [savingThumb, setSavingThumb] = useState<FeedbackThumb | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const displayedFeedback = feedbackDisabledReason === null ? feedback : null
  const { isCopying, isCopied, copyError, handleCopy } = useMessageCopy({
    copy: onCopy,
    analyticsEvent: AnalyticsEvent.CopyChatResponse,
    errorMessage: 'Response could not be copied. Try again.',
  })
  const actionError = error ?? copyError

  async function handleThumbClick(thumb: FeedbackThumb) {
    if (feedbackDisabledReason !== null || savingThumb) return

    const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
    setError(null)
    setSavingThumb(thumb)
    try {
      // Persist the rating before opening the optional-details dialog. If the user submits
      // the dialog, its second PUT enriches this same feedback record; dismissing keeps the thumb.
      const isCurrentRating = displayedFeedback?.thumb_rating === thumb
      const savedFeedback = await putAgentV2TurnFeedback({
        turnId,
        thumb,
        thumbText: isCurrentRating ? displayedFeedback.thumb_text : null,
        thumbReasons: isCurrentRating ? displayedFeedback.thumb_reasons : null,
      })
      if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

      onFeedbackChange(savedFeedback)
      trackEvent(thumb === FeedbackThumb.UP ? AnalyticsEvent.ResponseThumbUp : AnalyticsEvent.ResponseThumbDown)
      setDialogPositive(thumb === FeedbackThumb.UP)
    } catch {
      if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return
      setError('Feedback could not be saved. Try again.')
    } finally {
      setSavingThumb(null)
    }
  }

  async function handleDownload() {
    if (!onDownload || isDownloading) return
    setError(null)
    setIsDownloading(true)
    try {
      await onDownload()
    } catch {
      setError('Conversation could not be downloaded. Try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <>
      <div className="agent-v2-response-actions mt-2">
        <div className="agent-v2-response-actions-row flex items-center gap-1">
          {isFeedbackEligible && (
            <>
              <CustomizableTooltip title={feedbackDisabledReason ?? ''} placement="top" disableInteractive>
                <span className="inline-flex">
                  <IconButton
                    aria-label={feedbackDisabledReason ? `This response was helpful. ${feedbackDisabledReason}` : 'This response was helpful'}
                    aria-pressed={displayedFeedback?.thumb_rating === FeedbackThumb.UP}
                    className={clsx(displayedFeedback?.thumb_rating === FeedbackThumb.UP && 'bg-brand-neutral-200')}
                    color="neutral"
                    disabled={feedbackDisabledReason !== null || savingThumb !== null}
                    icon={<ThumbsUp />}
                    loading={feedbackDisabledReason === null && savingThumb === FeedbackThumb.UP}
                    onClick={() => handleThumbClick(FeedbackThumb.UP)}
                    size="sm"
                    title={feedbackDisabledReason ?? 'This response was helpful'}
                    type="button"
                    variant="plain"
                  />
                </span>
              </CustomizableTooltip>
              <CustomizableTooltip title={feedbackDisabledReason ?? ''} placement="top" disableInteractive>
                <span className="inline-flex">
                  <IconButton
                    aria-label={feedbackDisabledReason ? `This response was not helpful. ${feedbackDisabledReason}` : 'This response was not helpful'}
                    aria-pressed={displayedFeedback?.thumb_rating === FeedbackThumb.DOWN}
                    className={clsx(displayedFeedback?.thumb_rating === FeedbackThumb.DOWN && 'bg-brand-neutral-200')}
                    color="neutral"
                    disabled={feedbackDisabledReason !== null || savingThumb !== null}
                    icon={<ThumbsDown />}
                    loading={feedbackDisabledReason === null && savingThumb === FeedbackThumb.DOWN}
                    onClick={() => handleThumbClick(FeedbackThumb.DOWN)}
                    size="sm"
                    title={feedbackDisabledReason ?? 'This response was not helpful'}
                    type="button"
                    variant="plain"
                  />
                </span>
              </CustomizableTooltip>
            </>
          )}
          <IconButton
            aria-label="Copy response to clipboard"
            color="neutral"
            icon={isCopied ? <Copied /> : <Copy />}
            loading={isCopying}
            onClick={() => {
              setError(null)
              void handleCopy()
            }}
            size="sm"
            tooltipPlacement="bottom"
            tooltipText={isCopied ? 'Copied' : 'Copy'}
            type="button"
            variant="plain"
          />
          <MessageCopyStatus isCopied={isCopied} />
          {onDownload && (
            <IconButton
              aria-label="Download conversation"
              color="neutral"
              icon={<FileDownload />}
              loading={isDownloading}
              onClick={handleDownload}
              size="sm"
              title="Download conversation"
              type="button"
              variant="plain"
            />
          )}
        </div>
        {actionError && (
          <p className="mt-1 text-xs text-red-700" role="alert">
            {actionError}
          </p>
        )}
      </div>

      {researchContactOpen && !researchContactPromptCompleted && feedbackDisabledReason === null && (
        <ResearchContactDialog
          onClose={() => setResearchContactOpen(false)}
          onSubmit={async () => {
            await postResearchContact(turnId)
            dispatch(chatUxSlice.actions.setResearchContactPromptCompleted(true))
          }}
        />
      )}
      {dialogPositive !== null && feedbackDisabledReason === null && (
        <MessageFeedbackDialog
          conversationId={conversationId}
          positive={dialogPositive}
          visible
          onClose={() => setDialogPositive(null)}
          onSubmitted={() => {
            setDialogPositive(null)
            if (!researchContactPromptCompleted) setResearchContactOpen(true)
          }}
          onSubmit={async ({ thumb, thumbReasons, thumbText }) => {
            const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
            const savedFeedback = await putAgentV2TurnFeedback({
              turnId,
              thumb,
              thumbText,
              thumbReasons,
            })
            if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

            onFeedbackChange(savedFeedback)
          }}
        />
      )}
    </>
  )
}
